import { Lightning as lng, Log, Registry } from '@lightningjs/sdk';
import { GuideTimeline } from './GuideTimeline';
import { GuideChannel } from './GuideChannel';
import { CmsConstants } from '../../../Business/cms/CmsConstants';
import { EpgGuideDataSource } from '../../../Business/epg/guide/EpgGuideDataSource';
import { Theme } from '../../../config/theme';

const _constants = {
  timeLineHeight: 50,
  channelInfoWidth: 322,
  channelProgramsWidth: 1432,
  channelHeight: 80,
  channelSpacing: 30,
};

const itemPoolSize = 13;

export class EpgGuide extends lng.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      clipping: true,
      w: w => w,
      h: h => h,
      Timeline: {
        type: GuideTimeline,
        h: _constants.timeLineHeight,
      },
      ClipBox: {
        clipping: true,
        w: w => w,
        h: h => h - _constants.timeLineHeight,
        y: _constants.timeLineHeight,
        ContainerContainer: {
          y: CmsConstants.Card.borderSize,
          w: w => w,
          h: h => h - CmsConstants.Card.borderSize,
          Container: {
            w: w => w,
          },
        },
      },
      TimeStripContainer: {
        x: _constants.channelInfoWidth,
        y: _constants.timeLineHeight - 10,
        w: _constants.channelProgramsWidth,
        h: h => h - _constants.timeLineHeight + 10,
        TimeStrip: {
          x: 0,
          w: 4,
          h: h => h,
          rect: true,
          color: Theme.Colors.Guide.currentTime,
          visible: false,
        },
      },
    };
  }

  _init() {
    this.tag('Timeline').backend = this.backend; // give Timeline a backend
    this.dataIndex = 0;
    this.channelDatasources = [];

    const now = new Date();
    this.startTime = new Date(now.setMinutes(0, 0, 0));
    this.endTime = new Date(now.setHours(now.getHours() + 2, 0, 0, 0));
    Log.info('[EpgGuide] _init', this.startTime, this.endTime);
    this.tag('Timeline').updateTime(new Date(this.startTime), new Date(this.endTime));
    this.timeStripInterval = undefined;

    // make datasource that contains all the channels that need to be displayed
    this.datasource = new EpgGuideDataSource(this.backend);

    this.addInitialItems();
    this.datasource.loadChannels().then(channels => {
      this.channelDatasources = channels;
      this.tag('Container').h = channels.length * (_constants.channelHeight + _constants.channelSpacing);
      // fill items
      this.populate(0);
    });
  }

  _active() {
    this._positionTimeStrip();
  }

  _positionTimeStrip() {
    if (this.timeStripInterval) {
      Registry.clearInterval(this.timeStripInterval);
      this.timeStripInterval = undefined;
    }
    const now = new Date();
    if (this.startTime <= now && now <= this.endTime) {
      this.tag('TimeStrip').visible = true;
      const pos =
        ((now.getTime() - this.startTime.getTime()) / (this.endTime.getTime() - this.startTime.getTime())) *
        _constants.channelProgramsWidth;
      this.tag('TimeStrip').patch({ x: pos });
      this.timeStripInterval = Registry.setInterval(() => {
        if (this.startTime <= now && now <= this.endTime) {
          const now = new Date();
          const pos = Math.round(
            ((now.getTime() - this.startTime.getTime()) / (this.endTime.getTime() - this.startTime.getTime())) *
              _constants.channelProgramsWidth
          );
          this.tag('TimeStrip').patch({ x: pos });
          // this.tag('TimeStrip').setSmooth('x', pos, { duration: 1 });
        } else {
          Registry.clearInterval(this.timeStripInterval);
          this.timeStripInterval = undefined;
        }
      }, 30000);
    } else {
      this.tag('TimeStrip').visible = false;
    }
  }

  _onDetach() {
    super._onDetach();
    if (this.timeStripInterval) {
      Registry.clearInterval(this.timeStripInterval);
      this.timeStripInterval = undefined;
    }
  }

  $moveRight() {
    // update current time to 2 hours ahead
    this.startTime.setHours(this.startTime.getHours() + 2);
    this.endTime.setHours(this.endTime.getHours() + 2);
    this._positionTimeStrip();

    this.tag('Container').children.forEach(channel => {
      channel.moveRight();
    });
    this.tag('Timeline').moveRight(this.startTime);
  }

  $moveLeft() {
    // update current time to 2 hours back
    this.startTime.setHours(this.startTime.getHours() - 2);
    this.endTime.setHours(this.endTime.getHours() - 2);
    this._positionTimeStrip();

    this.tag('Container').children.forEach(channel => {
      channel.moveLeft();
    });
    this.tag('Timeline').moveLeft(this.startTime);
  }

  updateTime(start, end) {
    this.startTime = new Date(start);
    this.endTime = new Date(end);
    // give everything updated start and end times and force reload
    this.tag('Timeline').updateTime(new Date(start));
    this.tag('Container').children.forEach(channel => {
      channel.setTime(start, end);
    });
    this._positionTimeStrip();
  }

  /**
   * @param {IFiltersInfo} filter
   */
  updateFilterHighlights(filter) {
    this._filterState = filter;
    this.tag('Container').children.forEach(channel => {
      channel.updateFilterHighlights(filter);
    });
  }

  _handleDown() {
    let prevPos = this._getFocused().getCurrentPosition();

    let prevValue = this.dataIndex;
    this.dataIndex = Math.min(this.dataIndex + 1, this.channelDatasources.length - 1);

    if (prevValue !== this.dataIndex) {
      this.centerOnIndex(this.dataIndex);
      this._getFocused().setPreferedFocus(prevPos);
      this.moveFirstToLast();
      return true;
    }
    return false;
  }

  _handleUp() {
    let prevPos = this._getFocused().getCurrentPosition();

    let prevValue = this.dataIndex;
    this.dataIndex = Math.max(this.dataIndex - 1, 0);

    if (prevValue !== this.dataIndex) {
      this.centerOnIndex(this.dataIndex);
      this._getFocused().setPreferedFocus(prevPos);
      this.moveLastToFirst();
      return true;
    }
    return false;
  }

  _getFocused() {
    let container = this.tag('Container');
    return container.children.find(el => el.index === this.dataIndex) || container.childList.first;
  }

  _getFocusable() {
    return this.tag('Container').children;
  }

  calculateYPos(index) {
    return index * (_constants.channelHeight + _constants.channelSpacing);
  }

  centerOnIndex(index) {
    const target = this.finalH / 2 - 150;
    const channelCenter = this.calculateYPos(index) + _constants.channelHeight / 2;
    const result = Math.min(0, target - channelCenter);
    this.tag('Container').setSmooth('y', result);
  }

  addInitialItems() {
    // add GuideChannels to be reused
    for (let i = 0; i < itemPoolSize; i++) {
      const yPos = this.calculateYPos(i);
      this.tag('Container').childList.a({
        y: yPos,
        type: GuideChannel,
        backend: this.backend,
        datasource: undefined,
        fromUntil: { start: this.startTime, end: this.endTime },
        filterState: this._filterState,
      });
    }
    this.tag('Container').h = itemPoolSize * (_constants.channelHeight + _constants.channelSpacing);
  }

  populate(startIdx) {
    const { result: dses, offset } = this.getDsWindow(this.channelDatasources, startIdx, itemPoolSize);
    // add ds es and position correctly
    this.tag('Container').childList.forEach(
      /**
       * @param {GuideChannel} channel
       * @param {number} idx
       */
      (channel, idx) => {
        const itemIdx = offset + idx;
        if (itemIdx > -1) {
          channel.y = this.calculateYPos(itemIdx);
          channel.setNewData(dses[idx], itemIdx);
        } else {
          channel.visible = false;
        }
      }
    );
    // center
    this.dataIndex = startIdx;
    this.centerOnIndex(this.dataIndex);
    this._refocus();
  }

  /**
   * gets a datasource array of size 'size' that centers on the index or starts from beginning or end
   * @param {[]} array
   * @param index
   * @param size
   * @returns {{result: [], offset: number}}
   */
  getDsWindow(array, index, size) {
    const halfSize = (size - 1) / 2;
    if (array.length <= size) {
      return { result: array, offset: 0 };
    }
    if (index < halfSize) {
      return { result: array.slice(0, size), offset: 0 };
    } else if (index > array.length - 1 - halfSize) {
      return { result: array.slice(-size), offset: array.length - size };
    } else {
      return { result: array.slice(index - halfSize, index + halfSize + 1), offset: index - halfSize };
    }
  }

  moveFirstToLast() {
    const currentItems = this.tag('Container').children;
    const currentIndexes = currentItems.map(el => el.index);
    const minIdx = Math.min(...currentIndexes);
    const maxIdx = Math.max(...currentIndexes);
    if (maxIdx + 1 <= this.channelDatasources.length - 1) {
      const firstItem = currentItems.find(el => el.index === minIdx);
      if (Math.abs(firstItem.index - this.dataIndex) > 4) {
        // dont move first item when index is to low. Prevent first item fom disappearing when still on screen
        firstItem.y = this.calculateYPos(maxIdx + 1);
        firstItem.setNewData(this.channelDatasources[maxIdx + 1], maxIdx + 1);
      }
    }
  }

  moveLastToFirst() {
    const currentItems = this.tag('Container').children;
    const currentIndexes = currentItems.map(el => el.index);
    const minIdx = Math.min(...currentIndexes);
    const maxIdx = Math.max(...currentIndexes);
    if (minIdx > 0) {
      const lastItem = currentItems.find(el => el.index === maxIdx);
      if (Math.abs(lastItem.index - this.dataIndex) > 5) {
        // dont move last item when index is to high. Prevent last item fom disappearing when still on screen
        lastItem.y = this.calculateYPos(minIdx - 1);
        lastItem.setNewData(this.channelDatasources[minIdx - 1], minIdx - 1);
      }
    }
  }

  navigateToChannel(channelNumber) {
    const channelNumbers = this.channelDatasources.map(el => el.getChannelNumber());
    const closest = channelNumbers.reduce((prev, curr) => {
      return Math.abs(curr - channelNumber) < Math.abs(prev - channelNumber) ? curr : prev;
    });
    const foundIndex = channelNumbers.indexOf(closest);
    this.populate(foundIndex);
  }
}
